import { useState, useCallback, useEffect, useMemo } from "react";
import { useReservoirClient, useCoinConversion, useTokens, AcceptBidStep, } from "@reservoir0x/reservoir-kit-ui";
import { customChains, } from "@reservoir0x/reservoir-sdk";
import { parseUnits } from "viem";
import * as allChains from "viem/chains";
import { useConfig, useWalletClient } from "wagmi";
import { getAccount, switchChain } from "wagmi/actions";
import { useQuicksellCartContext } from "./QuickSellProvider";
const useAcceptBidCart = ({ tokens: inputTokens, chainId, feesOnTopCustom, }) => {
    var _a, _b;
    const [tokens, setTokens] = useState(inputTokens);
    const [stepData, setStepData] = useState(null);
    const [prices, setPrices] = useState([]);
    const [acceptBidStep, setAcceptBidStep] = useState(AcceptBidStep.Checkout);
    const [transactionError, setTransactionError] = useState();
    const { dispatch } = useQuicksellCartContext();
    useEffect(() => {
        if (!inputTokens || !tokens)
            return;
        if (JSON.stringify(inputTokens) === JSON.stringify(tokens))
            return;
        setTokens(inputTokens);
    }, [inputTokens, tokens]);
    const client = useReservoirClient();
    const currentChain = client === null || client === void 0 ? void 0 : client.currentChain();
    const config = useConfig();
    const rendererChain = chainId
        ? (client === null || client === void 0 ? void 0 : client.chains.find(({ id }) => id === chainId)) || currentChain
        : currentChain;
    const wagmiChain = Object.values({
        ...allChains,
        ...customChains,
    }).find(({ id }) => (rendererChain === null || rendererChain === void 0 ? void 0 : rendererChain.id) === id);
    const { data: wallet } = useWalletClient({ chainId: rendererChain === null || rendererChain === void 0 ? void 0 : rendererChain.id });
    const blockExplorerBaseUrl = ((_b = (_a = wagmiChain === null || wagmiChain === void 0 ? void 0 : wagmiChain.blockExplorers) === null || _a === void 0 ? void 0 : _a.default) === null || _b === void 0 ? void 0 : _b.url) || "https://etherscan.io";
    const [isFetchingBidPath, setIsFetchingBidPath] = useState(false);
    const [bidsPath, setBidsPath] = useState(null);
    const [feesOnTop, setFeesOnTop] = useState(null);
    const _tokenIds = useMemo(() => tokens
        .filter((token) => token.tokenId)
        .map((token) => {
        const contract = ((token === null || token === void 0 ? void 0 : token.collectionId) || "").split(":")[0];
        return `${contract}:${token.tokenId}`;
    }), [tokens]);
    const { data: tokensData, mutate: mutateTokens, isValidating: isFetchingTokenData, } = useTokens(_tokenIds &&
        _tokenIds.length > 0 && {
        tokens: _tokenIds,
        normalizeRoyalties: false,
    }, {
        revalidateFirstPage: true,
    }, rendererChain === null || rendererChain === void 0 ? void 0 : rendererChain.id);
    const enhancedTokens = useMemo(() => {
        const tokensDataMap = tokensData.reduce((map, data) => {
            var _a, _b;
            map[`${(_a = data.token) === null || _a === void 0 ? void 0 : _a.contract}:${(_b = data.token) === null || _b === void 0 ? void 0 : _b.tokenId}`] = data;
            return map;
        }, {});
        const tokensBidPathMap = (bidsPath === null || bidsPath === void 0 ? void 0 : bidsPath.reduce((map, path) => {
            const key = `${path.contract}:${path.tokenId}`;
            const mapPath = map[key];
            if (!mapPath) {
                map[key] = [path];
            }
            else {
                mapPath.push(path);
            }
            return map;
        }, {})) ||
            {};
        return tokens.reduce((enhancedTokens, token) => {
            var _a, _b, _c;
            const contract = token.collectionId.split(":")[0];
            const dataMapKey = `${contract}:${token.tokenId}`;
            const tokenData = tokensDataMap[dataMapKey];
            const bidIds = ((_a = token.bidIds) === null || _a === void 0 ? void 0 : _a.filter((bidId) => bidId.length > 0)) || [];
            const bidsPath = tokensBidPathMap[dataMapKey] ? tokensBidPathMap[dataMapKey] : [];
            if (!bidIds.length) {
                enhancedTokens.push({
                    ...token,
                    bidIds: ((_c = (_b = tokenData === null || tokenData === void 0 ? void 0 : tokenData.market) === null || _b === void 0 ? void 0 : _b.topBid) === null || _c === void 0 ? void 0 : _c.id)
                        ? [tokenData.market.topBid.id]
                        : [],
                    tokenData,
                    bidsPath,
                });
            }
            else {
                enhancedTokens.push({
                    ...token,
                    bidIds: token.bidIds || [],
                    tokenData,
                    bidsPath,
                });
            }
            return enhancedTokens;
        }, []);
    }, [tokensData, tokens, bidsPath]);
    const bidTokenMap = useMemo(() => enhancedTokens.reduce((map, token) => {
        token.bidIds.forEach((bidId) => {
            map[bidId] = token;
        });
        return map;
    }, {}), [enhancedTokens]);
    const fetchBidsPath = useCallback((tokens) => {
        if (!wallet || !client || !tokens.length) {
            setIsFetchingBidPath(false);
            return;
        }
        setIsFetchingBidPath(true);
        const options = {
            onlyPath: true,
            partial: true,
            normalizeRoyalties: false,
        };
        const items = tokens === null || tokens === void 0 ? void 0 : tokens.reduce((items, token) => {
            if (tokens) {
                const contract = token.collectionId.split(":")[0];
                const bids = token.bidIds
                    ? token.bidIds.filter((bid) => bid.length > 0)
                    : [];
                if (bids && bids.length > 0) {
                    bids.forEach((bidId) => {
                        items.push({
                            orderId: bidId,
                            token: `${contract}:${token.tokenId}`,
                            quantity: token.quantity,
                        });
                    });
                }
                else {
                    items.push({
                        token: `${contract}:${token.tokenId}`,
                        quantity: token.quantity,
                    });
                }
            }
            return items;
        }, []);
        const acceptOfferParams = {
            chainId: rendererChain === null || rendererChain === void 0 ? void 0 : rendererChain.id,
            items: items,
            wallet,
            options,
            precheck: true,
            onProgress: () => ({}),
        };
        client.actions
            .acceptOffer(acceptOfferParams)
            .then((data) => {
            if (feesOnTopCustom) {
                const bidsPath = "path" in data
                    ? data["path"]
                    : null;
                if (bidsPath) {
                    let feesOnTop = [];
                    if (feesOnTopCustom) {
                        feesOnTop = feesOnTopCustom(bidsPath) || [];
                    }
                    if (feesOnTop) {
                        acceptOfferParams.options.feesOnTop = feesOnTop;
                        setFeesOnTop(feesOnTop);
                    }
                    return client.actions.acceptOffer(acceptOfferParams);
                }
            }
            else {
                return data;
            }
        })
            .then((data) => {
            setBidsPath("path" in data
                ? data["path"]
                : null);
        })
            .finally(() => {
            setIsFetchingBidPath(false);
        });
    }, [client, wallet, rendererChain, feesOnTopCustom]);
    useEffect(() => {
        if (!client || !wallet)
            return;
        fetchBidsPath(tokens);
    }, [client, tokens, wallet]);
    const currencySymbols = useMemo(() => Array.from(enhancedTokens.reduce((symbols, { bidsPath }) => {
        bidsPath.forEach(({ sellOutCurrencySymbol, currencySymbol }) => {
            if (sellOutCurrencySymbol) {
                symbols.add(sellOutCurrencySymbol);
            }
            if (currencySymbol) {
                symbols.add(currencySymbol);
            }
        });
        return symbols;
    }, new Set())).join(","), [enhancedTokens]);
    const conversions = useCoinConversion(currencySymbols.length > 0 ? "USD" : undefined, currencySymbols);
    const usdPrices = useMemo(() => conversions.reduce((map, price) => {
        map[price.symbol] = price;
        return map;
    }, {}), [conversions]);
    const acceptBid = useCallback(async () => {
        setTransactionError(null);
        if (!wallet) {
            const error = new Error("Missing a wallet/signer");
            setTransactionError(error);
            throw error;
        }
        let activeWalletChain = getAccount(config).chain;
        if ((rendererChain === null || rendererChain === void 0 ? void 0 : rendererChain.id) !== (activeWalletChain === null || activeWalletChain === void 0 ? void 0 : activeWalletChain.id)) {
            activeWalletChain = await switchChain(config, {
                chainId: rendererChain === null || rendererChain === void 0 ? void 0 : rendererChain.id,
            });
        }
        if ((rendererChain === null || rendererChain === void 0 ? void 0 : rendererChain.id) !== (activeWalletChain === null || activeWalletChain === void 0 ? void 0 : activeWalletChain.id)) {
            const error = new Error(`Mismatching chainIds`);
            setTransactionError(error);
            throw error;
        }
        if (!bidsPath) {
            const error = new Error("Missing bids to accept");
            setTransactionError(error);
            throw error;
        }
        if (!client) {
            const error = new Error("ReservoirClient was not initialized");
            setTransactionError(error);
            setTransactionError(null);
            throw error;
        }
        const options = {
            partial: true,
            normalizeRoyalties: false,
        };
        if (feesOnTop) {
            options.feesOnTop = feesOnTop;
        }
        setAcceptBidStep(AcceptBidStep.ApproveMarketplace);
        const items = bidsPath.map(({ orderId, tokenId, contract, quantity }) => ({
            orderId: orderId,
            token: `${contract}:${tokenId}`,
            quantity,
        }));
        const expectedPrice = {};
        for (const currency in prices) {
            expectedPrice[currency] = {
                amount: prices[currency].netAmount,
                raw: parseUnits(`${prices[currency].netAmount}`, prices[currency].currency.decimals || 18),
                currencyAddress: prices[currency].currency.contract,
                currencyDecimals: prices[currency].currency.decimals || 18,
            };
        }
        let hasError = false;
        await client.actions
            .acceptOffer({
            chainId: rendererChain === null || rendererChain === void 0 ? void 0 : rendererChain.id,
            expectedPrice,
            wallet,
            items,
            options,
            onProgress: (steps, path) => {
                var _a, _b;
                if (!steps || hasError)
                    return;
                setBidsPath(path);
                const executableSteps = steps.filter((step) => step.items && step.items.length > 0);
                const stepCount = executableSteps.length;
                let currentStepItem;
                let currentStepIndex = 0;
                executableSteps.find((step, index) => {
                    var _a;
                    currentStepIndex = index;
                    currentStepItem = (_a = step.items) === null || _a === void 0 ? void 0 : _a.find((item) => item.status === "incomplete");
                    return currentStepItem;
                });
                const currentStep = currentStepIndex > -1
                    ? executableSteps[currentStepIndex]
                    : executableSteps[stepCount - 1];
                if (currentStepItem) {
                    setStepData({
                        totalSteps: stepCount,
                        currentStep,
                        currentStepItem,
                        steps,
                    });
                    if (currentStep.id === "auth") {
                        setAcceptBidStep(AcceptBidStep.Auth);
                    }
                    else if (currentStep.id === "nft-approval") {
                        setAcceptBidStep(AcceptBidStep.ApproveMarketplace);
                    }
                    else if (currentStep.id === "sale") {
                        if ((_a = currentStep.items) === null || _a === void 0 ? void 0 : _a.every((item) => item.txHashes !== undefined)) {
                            setAcceptBidStep(AcceptBidStep.Finalizing);
                        }
                        else {
                            setAcceptBidStep(AcceptBidStep.ApproveMarketplace);
                        }
                    }
                    else if (currentStep.id === "swap") {
                        setAcceptBidStep(AcceptBidStep.TokenSwap);
                    }
                }
                else if (executableSteps.every((step) => {
                    var _a;
                    return !step.items ||
                        step.items.length == 0 ||
                        ((_a = step.items) === null || _a === void 0 ? void 0 : _a.every((item) => item.status === "complete"));
                })) {
                    setAcceptBidStep(AcceptBidStep.Complete);
                    const lastStepItem = currentStep.items
                        ? currentStep.items[((_b = currentStep.items) === null || _b === void 0 ? void 0 : _b.length) - 1]
                        : undefined;
                    if (lastStepItem) {
                        setStepData({
                            totalSteps: stepCount,
                            steps,
                            currentStep,
                            currentStepItem: lastStepItem,
                        });
                    }
                }
            },
        })
            .catch((e) => {
            dispatch({
                type: "SET_ERROR",
                payload: { chainId, error: e },
            });
            hasError = true;
            setTransactionError(e);
            setAcceptBidStep(AcceptBidStep.Checkout);
            setStepData(null);
            fetchBidsPath(tokens);
            mutateTokens();
        });
    }, [
        config,
        bidsPath,
        bidTokenMap,
        rendererChain,
        client,
        wallet,
        prices,
        feesOnTop,
        mutateTokens,
    ]);
    useEffect(() => {
        if (bidsPath && bidsPath.length > 0) {
            const prices = bidsPath.reduce((map, { quote, sellOutCurrency, sellOutCurrencySymbol, sellOutQuote, currency, currencyDecimals, currencySymbol, builtInFees, feesOnTop, totalPrice, }) => {
                const netAmount = sellOutQuote || quote || 0;
                const amount = totalPrice || 0;
                let royalty = 0;
                let marketplaceFee = 0;
                let referralFee = 0;
                if (sellOutCurrency && sellOutCurrencySymbol) {
                    builtInFees === null || builtInFees === void 0 ? void 0 : builtInFees.forEach((fee) => {
                        switch (fee.kind) {
                            case "marketplace": {
                                marketplaceFee += fee.amount || 0;
                                break;
                            }
                            case "royalty": {
                                royalty += fee.amount || 0;
                                break;
                            }
                        }
                    });
                    feesOnTop === null || feesOnTop === void 0 ? void 0 : feesOnTop.forEach((fee) => {
                        switch (fee.kind) {
                            case "royalty": {
                                royalty += fee.amount || 0;
                                break;
                            }
                            case "marketplace":
                            default: {
                                referralFee += fee.amount || 0;
                                break;
                            }
                        }
                    });
                    if (!map[sellOutCurrencySymbol]) {
                        map[sellOutCurrencySymbol] = {
                            netAmount: netAmount - referralFee,
                            amount,
                            currency: {
                                contract: sellOutCurrency,
                                symbol: sellOutCurrencySymbol,
                                decimals: currencyDecimals,
                            },
                            royalty,
                            marketplaceFee,
                            feesOnTop: referralFee,
                        };
                    }
                    else if (map[sellOutCurrencySymbol]) {
                        map[sellOutCurrencySymbol].netAmount += netAmount - referralFee;
                        map[sellOutCurrencySymbol].amount += amount;
                        map[sellOutCurrencySymbol].royalty += royalty;
                        map[sellOutCurrencySymbol].marketplaceFee += marketplaceFee;
                        map[sellOutCurrencySymbol].feesOnTop += referralFee;
                    }
                }
                else if (currency && currencySymbol) {
                    builtInFees === null || builtInFees === void 0 ? void 0 : builtInFees.forEach((fee) => {
                        switch (fee.kind) {
                            case "marketplace": {
                                marketplaceFee += fee.amount || 0;
                                break;
                            }
                            case "royalty": {
                                royalty += fee.amount || 0;
                                break;
                            }
                        }
                    });
                    feesOnTop === null || feesOnTop === void 0 ? void 0 : feesOnTop.forEach((fee) => {
                        switch (fee.kind) {
                            case "royalty": {
                                royalty += fee.amount || 0;
                                break;
                            }
                            case "marketplace":
                            default: {
                                referralFee += fee.amount || 0;
                                break;
                            }
                        }
                    });
                    if (!map[currencySymbol]) {
                        map[currencySymbol] = {
                            netAmount: netAmount - referralFee,
                            amount,
                            currency: {
                                contract: currency,
                                symbol: currencySymbol,
                                decimals: currencyDecimals,
                            },
                            royalty,
                            marketplaceFee,
                            feesOnTop: referralFee,
                        };
                    }
                    else if (map[currencySymbol]) {
                        map[currencySymbol].netAmount += netAmount - referralFee;
                        map[currencySymbol].amount += amount;
                        map[currencySymbol].royalty += royalty;
                        map[currencySymbol].marketplaceFee += marketplaceFee;
                        map[currencySymbol].feesOnTop += referralFee;
                    }
                }
                return map;
            }, {});
            setPrices(Object.values(prices));
            if (acceptBidStep === AcceptBidStep.Unavailable) {
                setAcceptBidStep(AcceptBidStep.Checkout);
            }
        }
        else if (!isFetchingBidPath) {
            setPrices([]);
            setAcceptBidStep(AcceptBidStep.Unavailable);
        }
    }, [client, bidsPath, isFetchingBidPath]);
    const swapCurrency = useMemo(() => {
        const bidPath = bidsPath === null || bidsPath === void 0 ? void 0 : bidsPath[0];
        if (bidPath && bidPath.sellOutCurrency) {
            return {
                contract: bidPath.sellOutCurrency,
                decimals: bidPath.sellOutCurrencyDecimals,
                symbol: bidPath.sellOutCurrencySymbol,
            };
        }
        else
            return null;
    }, [bidsPath]);
    // useEffect(() => {
    //   if (!open) {
    //     setAcceptBidStep(AcceptBidStep.Checkout)
    //     setStepData(null)
    //     setTransactionError(null)
    //   }
    // }, [open])
    // open
    //   ? (axios.defaults.headers.common['x-rkui-context'] =
    //     'acceptBidModalRenderer')
    //   : delete axios.defaults.headers.common?.['x-rkui-context']
    return {
        swapCurrency,
        loading: isFetchingBidPath || isFetchingTokenData,
        tokensData: enhancedTokens,
        acceptBidStep,
        transactionError,
        usdPrices,
        prices,
        blockExplorerBaseUrl,
        acceptBid,
        setAcceptBidStep,
        stepData,
    };
};
export default useAcceptBidCart;
